// ----------------------------------------------------------------------

function path(root: string, sublink: string) {
  return `${root}${sublink}`;
}

const ROOTS_AUTH = '/auth';
const ROOTS_DASHBOARD = '';

// ----------------------------------------------------------------------

export const PATH_AUTH = {
  root: ROOTS_AUTH,
  login: path(ROOTS_AUTH, '/login'),
  register: path(ROOTS_AUTH, '/register'),
  loginUnprotected: path(ROOTS_AUTH, '/login-unprotected'),
  registerUnprotected: path(ROOTS_AUTH, '/register-unprotected'),
  verify: path(ROOTS_AUTH, '/verify'),
  resetPassword: path(ROOTS_AUTH, '/reset-password'),
  newPassword: path(ROOTS_AUTH, '/new-password'),
};

export const PATH_PAGE = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page403: '/403',
  page404: '/404',
  page500: '/500',
  components: '/components',
};

export const PATH_DASHBOARD = {
  root: '/dashboard',
  general: {
    app: path(ROOTS_DASHBOARD, '/dashboard'),
    businessCustomers: path(ROOTS_DASHBOARD, '/business_customers'),
    businessCustomerEdit: (id: string) => path('', `/business_customers/${id}/edit`),
    businessCustomerCreate: path('', `/business_customers/create`),
  },
  projects: {
    root: path('', '/projects'),
    project: (projectCode: string) => path('', `/projects/${projectCode}`),
  },
  baskets: {
    root: path('', '/portfolios'),
    basket: (basketId: string) => path('', `/portfolios/${basketId}`),
  },
  // end
  permissionDenied: path(ROOTS_DASHBOARD, '/permission-denied'),
  user: {
    root: path(ROOTS_DASHBOARD, '/user'),
    account: path(ROOTS_DASHBOARD, '/account'),
    plans: path(ROOTS_DASHBOARD, '/account/plans'),
    confirmation: path(ROOTS_DASHBOARD, '/account/plans/confirmation'),
    cancelConfirmation: path(ROOTS_DASHBOARD, '/account/plans/confirmation/cancel'),
  },
  credits: {
    purchase: path('', '/credits/purchase'),
    purchaseHistory: path('',  `/credits/purchase-history`),
    purchaseCredits: path('', '/credits/purchase-credits'),
  },
  cart: {
    root: path('', '/cart'),
    checkout: path('', '/checkout'),
    confirmation: (id: string) => path('', `/checkout/complete/${id}`),
    requestQuote: path('', '/cart/request-quote'),
  },
  offsets: {
    transactions: path('', '/my-offsets/transactions'),
    transaction: (id: string) => path('', `/my-offsets/transactions/${id}`),
    footprints: path('', '/my-offsets/footprints'),
    event: {
      amount: path('', '/my-offsets/footprints/event-amount'),
      calculate: path('', '/my-offsets/footprints/event-calculator'),
      edit: (footprintId: string) => path('', `/my-offsets/footprints/event-calculator/${footprintId}/edit`),
      view: (footprintId: string) => path('', `/my-offsets/footprints/event-calculator/${footprintId}`),
      amountEdit: (footprintId: string) => path('', `/my-offsets/footprints/event-amount/${footprintId}/edit`),
      amountView: (footprintId: string) => path('', `/my-offsets/footprints/event-amount/${footprintId}`),
    },
    business: {
      amount: path('', '/my-offsets/footprints/business-amount'),
      calculate: path('', '/my-offsets/footprints/business-calculator'),
      edit: (footprintId: string) => path('', `/my-offsets/footprints/business-calculator/${footprintId}/edit`),
      view: (footprintId: string) => path('', `/my-offsets/footprints/business-calculator/${footprintId}`),
      amountEdit: (footprintId: string) => path('', `/my-offsets/footprints/business-amount/${footprintId}/edit`),
      amountView: (footprintId: string) => path('', `/my-offsets/footprints/business-amount/${footprintId}`),
    },
  },
  communications: {
    projects: path('', '/communications/supported-projects'),
    impact: path('', '/communications/impact'),
    merchantReport: (dateFrom: string, dateTo: string) => path('', `/communications/impact/${dateFrom}/${dateTo}/report`),
    landingPages: path('', '/communications/pages'),
    createImpactPages: (type: string) => path('', `/communications/pages/create?type=${type}`),
    updateImpactPages: (type: string, id: string) => path('', `/communications/pages/update?type=${type}&id=${id}`),
    widgets: path('', '/communications/widgets'),
    widgetsItem: (code: string) => path('', `/communications/widgets/${code}`),
    impactWidget: path('', '/communications/widgets/impact-widget'),
  },
  programs: {
    businessCustomers: path(ROOTS_DASHBOARD, '/programmes/business-customers'),
    businessCustomerEdit: (id: string) => path('', `/programmes/business-customers/${id}/edit`),
    businessCustomerCreate: path('', `/programmes/business-customers/create`),
    toolkit: path('', '/programmes/toolkit'),
    viewToolkitItem: (code: string) => path('', `/programmes/toolkit/${code}`),
    individuals: path('', '/programmes/individuals'),
    //
    participants: (type: string, code: string) => path('', `/programmes/${type}/${code}/participants`),
    overview: (type: string, code: string) => path('', `/programmes/${type}/${code}/overview`),
    insights: (type: string, code: string) => path('', `/programmes/${type}/${code}/insights`),
    settings: (type: string, code: string) => path('', `/programmes/${type}/${code}/settings`),
    offsets: (type: string, code: string) => path('', `/programmes/${type}/${code}/offsets`),

    // TODO: @marina clean up when epic is done
    contributions: (type: string, code: string) => path('', `/programmes/${type}/${code}/contributions`),
    emissions: (type: string, code: string) => path('', `/programmes/${type}/${code}/emissions`),
    emissionRecord: (type: string, code: string, emissionRecordId: string) => path('', `/programmes/${type}/${code}/emissions/${emissionRecordId}`),
    calculate: (type: string, code: string) => path('', `/programmes/${type}/${code}/emissions/calculate`),
    addEmission: (type: string, code: string, emissionRecordId: string) => path('', `/programmes/${type}/${code}/emissions/calculate/${emissionRecordId}`),
    program: (type: string, code: string) => path('', `/programmes/${type}/${code}`),
    transactions: (type: string, code: string, queryParameters?: URLSearchParams) => path('', `/programmes/${type}/${code}/offsets${queryParameters ? `?${queryParameters.toString()}` : ''}`),
    transaction: (type: string, code: string, id: string) => path('', `/programmes/${type}/${code}/offsets/${id}`),
    employees: (type: string, code: string) => path('', `/programmes/${type}/${code}/employees`),
    customers: (type: string, code: string) => path('', `/programmes/${type}/${code}/customers`),
    flights: (type: string, code: string) => path('', `/programmes/${type}/${code}/flights`),
    impact: (type: string, code: string) => path('', `/programmes/${type}/${code}/impact`),
    eventImpact: path('', `/programmes/event/impact`),
    engagement: (type: string, code: string) => path('', `/programmes/${type}/${code}/engagement`),
    configuration: (type: string, code: string) => path('', `/programmes/${type}/${code}/configuration`),
    report: (type: string, code: string, dateFrom: string, dateTo: string) => path('', `/programmes/${type}/${code}/impact/${dateFrom}/${dateTo}/report`),
    //
    viewReceipt: (id: string) => path('', `/offsets/${id}/receipt`),
    viewCertificate: (id: string) => path('', `/offsets/${id}/certificate`),
    viewDetailedCertificate: (id: string) => path('', `/offsets/${id}/detailed-certificate`),

    flightCalculatorSettingsPage: path('', '/programmes/toolkit/flight-calculator'),

    customer: {
      root: path('', '/programmes/customer'),
      contributions: path('', '/programmes/customer/contributions'),
    },
  },
  settings: {
    root: path('', '/settings'),
    theme: path('', '/settings/theme'),
    business: path('', '/settings/business'),
    defaultBasket: path('', '/settings/default-basket'),
    billing: path('', '/settings/billing'),
    menu: path('', '/settings/menu'),
    emails: path('', '/settings/emails'),
    dashboard: path('', '/settings/dashboard'),
  },
  account: {
    root: path('', '/account'),
    plans: path('', '/account/plans'),
    accountDetails: path('', '/account/account-details'),
    userManagement: path('', '/account/user-management')
  },
};
